import axios from 'axios';
import React, { ChangeEvent, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import CardHeader from 'react-bootstrap/CardHeader';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import {
    FaFileCsv,
    FaFileImport,
    FaFilePdf,
    FaRegCircleXmark,
    FaRegFloppyDisk,
    FaRegPenToSquare,
    FaRegSquarePlus,
    FaRegTrashCan,
} from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { downloadCsv, parseCsv } from '../../../../../helpers/csv';
import { IGuest, IMeeting } from '../../../../../types';
import { IconWrapper } from '../../../../IconWrapper/IconWrapper';

import styles from './Guests.module.scss';

interface IGuestsProps {
  meeting: IMeeting;
  onChange: () => void;
}

const Guests = ({ meeting, onChange }: IGuestsProps) => {
  const [newGender, setNewGender] = useState<'female' | 'male' | 'other'>('other');
  const [newFirstname, setNewFirstname] = useState<string>('');
  const [newLastname, setNewLastname] = useState<string>('');
  const [newFunction, setNewFunction] = useState<string>('');
  const [newAddress, setNewAddress] = useState<string>('');
  const [newCity, setNewCity] = useState<string>('');
  const [newType, setNewType] = useState<'official' | 'other'>('other');
  const [updateGuestId, setUpdateGuestId] = useState<string>('');
  const [updatedGender, setUpdatedGender] = useState<'female' | 'male' | 'other'>('other');
  const [updatedFirstname, setUpdatedFirstname] = useState<string>('');
  const [updatedLastname, setUpdatedLastname] = useState<string>('');
  const [updatedFunction, setUpdatedFunction] = useState<string>('');
  const [updatedAddress, setUpdatedAddress] = useState<string>('');
  const [updatedCity, setUpdatedCity] = useState<string>('');
  const [updatedType, setUpdatedType] = useState<'official' | 'other'>('other');
  const [selectedGuestIds, setSelectedGuestIds] = useState<string[]>([]);

  const handleSave = () => {
    axios
      .post(`/api/meeting/${meeting._id}/guest`, {
        gender: newGender,
        firstname: newFirstname,
        lastname: newLastname,
        function: newFunction,
        address: newAddress,
        city: newCity,
        type: newType,
      })
      .then(() => {
        setNewGender('female');
        setNewFirstname('');
        setNewLastname('');
        setNewFunction('');
        setNewAddress('');
        setNewCity('');
        setNewType('other');
        onChange();
      })
      .catch((err) => toast.error(err.response.data.message));
  };

  const handleImport = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    file.text()
      .then((text) => {
        const rows = parseCsv(text);
        if (rows.some((row) =>
          !['female', 'male', 'other'].includes(row.gender) ||
          !row.address || !row.city || !['official', 'other'].includes(row.type)
        )) {
          throw new Error('CSV guests must contain valid gender, address, city and type columns');
        }
        return axios.post(`/api/meeting/${meeting._id}/guest/import`, rows);
      })
      .then(() => onChange())
      .catch((error) => toast.error(error.response?.data ?? error.message ?? 'Unable to import guests'))
      .finally(() => {
        event.target.value = '';
      });
  };

  const handleExport = () => {
    downloadCsv(
      `${meeting.name}-guests.csv`,
      ['gender', 'firstname', 'lastname', 'function', 'address', 'city', 'type', 'response', 'present'],
      meeting.guests.map((guest) => [
        guest.gender,
        guest.firstname,
        guest.lastname,
        guest.function,
        guest.address,
        guest.city,
        guest.type,
        guest.response,
        guest.present,
      ])
    );
  };

  const handleDelete = (id: string) => () => {
    axios
      .delete(`/api/meeting/${meeting._id}/guest/${id}`)
      .then(() => {
        onChange();
      })
      .catch((err) => toast.error(err.response.data.message));
  };

  const handleBulkDelete = () => {
    if (selectedGuestIds.length === 0 || !window.confirm(`Delete ${selectedGuestIds.length} guests?`)) return;
    axios
      .post(`/api/meeting/${meeting._id}/guest/delete`, { ids: selectedGuestIds })
      .then(() => {
        setSelectedGuestIds([]);
        onChange();
      })
      .catch((err) => toast.error(err.response?.data ?? 'Unable to delete guests'));
  };

  const toggleGuest = (id: string) => {
    setSelectedGuestIds((current) =>
      current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id]
    );
  };

  const toggleAllGuests = () => {
    setSelectedGuestIds(
      selectedGuestIds.length === meeting.guests.length ? [] : meeting.guests.map((guest) => guest._id)
    );
  };

  const handleEdit = (guest: IGuest | undefined) => () => {
    setUpdateGuestId(guest ? guest._id : '');

    if (guest) {
      setUpdatedGender(guest.gender);
      setUpdatedFirstname(guest.firstname);
      setUpdatedLastname(guest.lastname);
      setUpdatedFunction(guest.function);
      setUpdatedAddress(guest.address);
      setUpdatedCity(guest.city);
      setUpdatedType(guest.type);
    } else {
      setUpdatedGender('female');
      setUpdatedFirstname('');
      setUpdatedLastname('');
      setUpdatedFunction('');
      setUpdatedAddress('');
      setUpdatedCity('');
      setUpdatedType('other');
    }
  };

  const handleMemberResponseChange =
    (guest: IGuest, type: 'response' | 'present') => (e: ChangeEvent<HTMLInputElement>) => {
      const updatedGuest = guest;
      updatedGuest[type] = !updatedGuest[type];

      axios
        .put(`/api/meeting/${meeting._id}/guest/${guest._id}`, updatedGuest)
        .then(() => {
          onChange();
        })
        .catch((err) => toast.error(err.response.data.message));
    };

  const handleEditSaveChange = (guest: IGuest) => () => {
    const updatedGuest = guest;
    updatedGuest.gender = updatedGender;
    updatedGuest.firstname = updatedFirstname;
    updatedGuest.lastname = updatedLastname;
    updatedGuest.function = updatedFunction;
    updatedGuest.address = updatedAddress;
    updatedGuest.city = updatedCity;
    updatedGuest.type = updatedType;

    axios.put(`/api/meeting/${meeting._id}/guest/${guest._id}`, updatedGuest).then(() => {
      setUpdateGuestId('');
      setUpdatedGender('female');
      setUpdatedFirstname('');
      setUpdatedLastname('');
      setUpdatedFunction('');
      setUpdatedAddress('');
      setUpdatedCity('');
      setUpdatedType('other');
      onChange();
    });
  };

  const getDocument = (document: string, id: string) => () => {
    axios
      .get(`/api/meeting/${meeting._id}/document/${document}/${id}`, {
        responseType: 'blob',
      })
      .then((response) => {
        //Create a Blob from the PDF Stream
        const file = new Blob([response.data], { type: 'application/pdf' });
        //Build a URL from the file
        const fileURL = URL.createObjectURL(file);
        //Open the URL on new Window
        const pdfWindow = window.open();
        if (pdfWindow !== null) pdfWindow.location.href = fileURL;
      })
      .catch((err) => toast.error(err.response.data.message));
  };

  const sortByLastname = (a: IGuest, b: IGuest) => {
    if (a.lastname < b.lastname) {
      return -1;
    }
    if (a.lastname > b.lastname) {
      return 1;
    }
    return 0;
  };

  return (
    <>
      <Card>
        <CardHeader className="d-flex justify-content-between">
          <Col>Guests ({meeting.guests.length})</Col>
          <Col className="d-flex justify-content-end gap-2">
            <Button size="sm" variant="danger" onClick={handleBulkDelete} disabled={selectedGuestIds.length === 0}>
              Delete selected ({selectedGuestIds.length})
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={toggleAllGuests}>
              {selectedGuestIds.length === meeting.guests.length ? 'Clear selection' : 'Select all'}
            </Button>
            <Button size="sm" variant="secondary" onClick={handleExport}>
              <IconWrapper icon={FaFileCsv} /> Export CSV
            </Button>
            <Form.Label className="btn btn-secondary btn-sm mb-0">
              <IconWrapper icon={FaFileImport} /> Import CSV
              <Form.Control type="file" accept=".csv,text/csv" onChange={handleImport} hidden />
            </Form.Label>
          </Col>
        </CardHeader>
        <Card.Body>
          <Table hover bordered size="sm" className="text-center">
            <thead className="align-middle">
              <tr>
                <th rowSpan={2} className={styles.wsmall}>Select</th>
                <th colSpan={7}>Personnal information</th>
                <th colSpan={2}>Response</th>
                <th rowSpan={2} className={styles.wsmall}>
                  Actions
                </th>
              </tr>
              <tr>
                <th className={styles.wsmall}>Gender</th>
                <th className={styles.wlarge}>First name</th>
                <th className={styles.wlarge}>Last name</th>
                <th className={styles.wlarge}>Function</th>
                <th className={styles.wlarge}>Address</th>
                <th className={styles.wlarge}>City</th>
                <th className={styles.wsmall}>Type</th>
                <th className={styles.wsmall}>Response</th>
                <th className={styles.wsmall}>Present</th>
              </tr>
            </thead>
            <tbody className="align-middle">
              {meeting.guests.length === 0 && (
                <tr>
                  <td colSpan={11} className="table-light">
                    No guests
                  </td>
                </tr>
              )}
              {meeting.guests.length > 0 && (
                <>
                  {meeting.guests.filter((g) => g.type === 'official').length > 0 && (
                    <>
                      <tr>
                        <td colSpan={11} className="table-warning">
                          Officials
                        </td>
                      </tr>
                      {meeting.guests
                        .sort(sortByLastname)
                        .filter((g) => g.type === 'official')
                        .map((guest, id) => (
                          <React.Fragment key={guest._id}>
                            {guest._id !== updateGuestId && (
                              <tr className={id % 2 ? '' : styles.stripe}>
                                <td>
                                  <Form.Check
                                    checked={selectedGuestIds.includes(guest._id)}
                                    onChange={() => toggleGuest(guest._id)}
                                    aria-label={`Select ${guest.firstname} ${guest.lastname}`}
                                  />
                                </td>
                                <td className={styles.capitalize}>{guest.gender}</td>
                                <td>{guest.firstname}</td>
                                <td>{guest.lastname}</td>
                                <td>{guest.function}</td>
                                <td>{guest.address}</td>
                                <td>{guest.city}</td>
                                <td className={styles.capitalize}>{guest.type}</td>
                                <td>
                                  <Form.Check
                                    checked={guest.response ?? false}
                                    onChange={handleMemberResponseChange(guest, 'response')}
                                    disabled={meeting.parameters.global.block_responses}
                                  />
                                </td>
                                <td>
                                  <Form.Check
                                    checked={guest.present ?? false}
                                    onChange={handleMemberResponseChange(guest, 'present')}
                                    disabled={meeting.parameters.global.block_responses}
                                  />
                                </td>
                                <td>
                                  <ButtonGroup aria-label="Actions">
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={getDocument('letters/officials', guest._id)}
                                      title="Letter"
                                    >
                                      <IconWrapper icon={FaFilePdf} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="warning"
                                      onClick={handleEdit(guest)}
                                      title="Edit"
                                    >
                                      <IconWrapper icon={FaRegPenToSquare} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      onClick={handleDelete(guest._id)}
                                      title="Delete"
                                      disabled={
                                        meeting.parameters.global.block_responses ||
                                        meeting.parameters.global.block_candidacies
                                      }
                                    >
                                      <IconWrapper icon={FaRegTrashCan} />
                                    </Button>
                                  </ButtonGroup>
                                </td>
                              </tr>
                            )}
                            {guest._id === updateGuestId && (
                              <tr>
                                <td>
                                  <Form.Check
                                    checked={selectedGuestIds.includes(guest._id)}
                                    onChange={() => toggleGuest(guest._id)}
                                    aria-label={`Select ${guest.firstname} ${guest.lastname}`}
                                  />
                                </td>
                                <td>
                                  <td>
                                    <Form.Select
                                      aria-label="Select gender"
                                      size="sm"
                                      onChange={(e) =>
                                        setUpdatedGender(
                                          e.target.value === 'male' ? 'male' : 'female'
                                        )
                                      }
                                      className="text-center"
                                      value={updatedGender}
                                      disabled={meeting.parameters.global.block_responses}
                                    >
                                      <option value="female">Female</option>
                                      <option value="male">Male</option>
                                    </Form.Select>
                                  </td>
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedFirstname}
                                    onChange={(e) => setUpdatedFirstname(e.target.value)}
                                    placeholder="First name"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedLastname}
                                    onChange={(e) => setUpdatedLastname(e.target.value)}
                                    placeholder="Last name"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedFunction}
                                    onChange={(e) => setUpdatedFunction(e.target.value)}
                                    placeholder="Function"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedAddress}
                                    onChange={(e) => setUpdatedAddress(e.target.value)}
                                    placeholder="Address"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedCity}
                                    onChange={(e) => setUpdatedCity(e.target.value)}
                                    placeholder="City"
                                  />
                                </td>
                                <td className={styles.capitalize}>{guest.type}</td>
                                <td>
                                  <Form.Check
                                    checked={guest.response ?? false}
                                    onChange={handleMemberResponseChange(guest, 'response')}
                                    disabled
                                  />
                                </td>
                                <td>
                                  <Form.Check
                                    checked={guest.present ?? false}
                                    onChange={handleMemberResponseChange(guest, 'present')}
                                    disabled
                                  />
                                </td>
                                <td>
                                  <ButtonGroup aria-label="Actions">
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={handleEditSaveChange(guest)}
                                      title="Save"
                                    >
                                      <IconWrapper icon={FaRegFloppyDisk} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={handleEdit(undefined)}
                                      title="Cancel"
                                    >
                                      <IconWrapper icon={FaRegCircleXmark} />
                                    </Button>
                                  </ButtonGroup>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                    </>
                  )}
                  {meeting.guests.filter((g) => g.type === 'other').length > 0 && (
                    <>
                      <tr>
                        <td colSpan={11} className="table-warning">
                          Others
                        </td>
                      </tr>
                      {meeting.guests
                        .sort(sortByLastname)
                        .filter((g) => g.type === 'other')
                        .map((guest, id) => (
                          <React.Fragment key={guest._id}>
                            {guest._id !== updateGuestId && (
                              <tr className={id % 2 ? '' : styles.stripe}>
                                <td>
                                  <Form.Check
                                    checked={selectedGuestIds.includes(guest._id)}
                                    onChange={() => toggleGuest(guest._id)}
                                    aria-label={`Select ${guest.firstname} ${guest.lastname}`}
                                  />
                                </td>
                                <td className={styles.capitalize}>{guest.gender}</td>
                                <td>{guest.firstname}</td>
                                <td>{guest.lastname}</td>
                                <td>{guest.function}</td>
                                <td>{guest.address}</td>
                                <td>{guest.city}</td>
                                <td className={styles.capitalize}>{guest.type}</td>
                                <td>
                                  <Form.Check
                                    checked={guest.response ?? false}
                                    onChange={handleMemberResponseChange(guest, 'response')}
                                    disabled={meeting.parameters.global.block_responses}
                                  />
                                </td>
                                <td>
                                  <Form.Check
                                    checked={guest.present ?? false}
                                    onChange={handleMemberResponseChange(guest, 'present')}
                                    disabled={meeting.parameters.global.block_responses}
                                  />
                                </td>
                                <td>
                                  <ButtonGroup aria-label="Actions">
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={getDocument('letters/others', guest._id)}
                                      title="Letter"
                                    >
                                      <IconWrapper icon={FaFilePdf} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="warning"
                                      onClick={handleEdit(guest)}
                                      title="Edit"
                                    >
                                      <IconWrapper icon={FaRegPenToSquare} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      onClick={handleDelete(guest._id)}
                                      title="Delete"
                                      disabled={
                                        meeting.parameters.global.block_responses ||
                                        meeting.parameters.global.block_candidacies
                                      }
                                    >
                                      <IconWrapper icon={FaRegTrashCan} />
                                    </Button>
                                  </ButtonGroup>
                                </td>
                              </tr>
                            )}
                            {guest._id === updateGuestId && (
                              <tr>
                                <td>
                                  <Form.Check
                                    checked={selectedGuestIds.includes(guest._id)}
                                    onChange={() => toggleGuest(guest._id)}
                                    aria-label={`Select ${guest.firstname} ${guest.lastname}`}
                                  />
                                </td>
                                <td>
                                  <td>
                                    <Form.Select
                                      aria-label="Select gender"
                                      size="sm"
                                      onChange={(e) =>
                                        setUpdatedGender(
                                          e.target.value === 'male' ? 'male' : 'female'
                                        )
                                      }
                                      className="text-center"
                                      value={updatedGender}
                                      disabled={meeting.parameters.global.block_responses}
                                    >
                                      <option value="female">Female</option>
                                      <option value="male">Male</option>
                                    </Form.Select>
                                  </td>
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedFirstname}
                                    onChange={(e) => setUpdatedFirstname(e.target.value)}
                                    placeholder="First name"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedLastname}
                                    onChange={(e) => setUpdatedLastname(e.target.value)}
                                    placeholder="Last name"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedFunction}
                                    onChange={(e) => setUpdatedFunction(e.target.value)}
                                    placeholder="Function"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedAddress}
                                    onChange={(e) => setUpdatedAddress(e.target.value)}
                                    placeholder="Address"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    className="text-center"
                                    value={updatedCity}
                                    onChange={(e) => setUpdatedCity(e.target.value)}
                                    placeholder="City"
                                  />
                                </td>
                                <td className={styles.capitalize}>{guest.type}</td>
                                <td>
                                  <Form.Check
                                    checked={guest.response ?? false}
                                    onChange={handleMemberResponseChange(guest, 'response')}
                                    disabled
                                  />
                                </td>
                                <td>
                                  <Form.Check
                                    checked={guest.present ?? false}
                                    onChange={handleMemberResponseChange(guest, 'present')}
                                    disabled
                                  />
                                </td>
                                <td>
                                  <ButtonGroup aria-label="Actions">
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={handleEditSaveChange(guest)}
                                      title="Save"
                                    >
                                      <IconWrapper icon={FaRegFloppyDisk} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={handleEdit(undefined)}
                                      title="Cancel"
                                    >
                                      <IconWrapper icon={FaRegCircleXmark} />
                                    </Button>
                                  </ButtonGroup>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                    </>
                  )}
                </>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td>
                  <Form.Select
                    aria-label="Select gender"
                    size="sm"
                    onChange={(e) => setNewGender(e.target.value === 'male' ? 'male' : e.target.value === 'female' ? 'female' : 'other')}
                    className="text-center"
                    value={newGender}
                    disabled={meeting.parameters.global.block_responses}
                  >
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newFirstname}
                    onChange={(e) => setNewFirstname(e.target.value)}
                    placeholder="First name"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newLastname}
                    onChange={(e) => setNewLastname(e.target.value)}
                    placeholder="Last name"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newFunction}
                    onChange={(e) => setNewFunction(e.target.value)}
                    placeholder="Function"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    placeholder="Address"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    placeholder="City"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  />
                </td>
                <td>
                  <Form.Select
                    aria-label="Select type"
                    size="sm"
                    onChange={(e) =>
                      setNewType(e.target.value === 'official' ? 'official' : 'other')
                    }
                    className="text-center"
                    value={newType}
                    disabled={meeting.parameters.global.block_responses}
                  >
                    <option value="official">Official</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </td>
                <td colSpan={2}></td>
                <td>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    title="Add"
                    disabled={
                      meeting.parameters.global.block_responses ||
                      meeting.parameters.global.block_candidacies
                    }
                  >
                    <IconWrapper icon={FaRegSquarePlus} />
                  </Button>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

export default Guests;
